import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { useGetMeetingLinkQuery } from '@/core/services/meeting-link'

import { useRequestHandler } from '@/hooks/useRequestHandler'

import { useAppDispatch, useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import { selectMeetingLinkUpdateRequestTracker, updateMeetingLink } from '@/redux/reducers/meeting-link.reducer'
import { getSpecialityOptions } from '@/redux/reducers/speciality-option.reducer'

import MeetingLinkInfo, { MeetingLinkInfoFormValues } from '../components/MeetingLinkInfo'

export default function MeetingLinkInfoContainer() {
  const dispatch = useAppDispatch()
  const { meetingLinkId } = useParams()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const [editable, setEditable] = useState(false)

  const authUser = useAppSelector(selectAuthUser)

  const { data: meetingLink } = useGetMeetingLinkQuery(meetingLinkId ?? '', { skip: !meetingLinkId })

  const meetingLinkInfo = useMemo(() => {
    return {
      busyEnabled: meetingLink?.busyEnabled ?? false,
      description: meetingLink?.description ?? '',
      name: meetingLink?.name ?? '',
      specialities:
        meetingLink?.specialities.map((speciality) => ({
          ...speciality,
          deletable: !meetingLink.availabilities.some((availability) => availability.specialities.includes(speciality.name)),
        })) ?? [],
      immediateConsultationConfig: meetingLink?.immediateConsultationConfig ?? {
        enabled: false,
        maxWaitingPatients: null,
        requirePaymentUpfront: true,
        price: null,
        paymentReceiver: null,
      },
      waitingRoom: (typeof meetingLink?.waitingRoom === 'string' ? meetingLink.waitingRoom : meetingLink?.waitingRoom?._id) ?? null,
    }
  }, [meetingLink])

  const updateMeetingLinkRequestHandler = useRequestHandler({
    onSuccess: () => {
      enqueueSnackbar(t('notification.meetingLink.update.success'), { variant: 'success' })
      setEditable(false)
    },
    onError: () => {
      enqueueSnackbar(t('notification.meetingLink.update.error'), { variant: 'error' })
    },
    requestTrackerSelector: selectMeetingLinkUpdateRequestTracker,
  })

  useEffect(() => {
    dispatch(getSpecialityOptions())
  }, [dispatch])

  const handleSubmit = (values: MeetingLinkInfoFormValues) => {
    if (!authUser || !meetingLinkId) return

    const { immediateConsultationConfig, ...rest } = values
    updateMeetingLinkRequestHandler.dispatch(
      updateMeetingLink(meetingLinkId, {
        ...rest,
        specialities: values.specialities.map((speciality) => ({
          name: speciality.name,
          code: speciality.code,
          price: speciality.price,
        })),
        immediateConsultationConfig,
        waitingRoom: values.waitingRoom || undefined,
      })
    )
  }

  return <MeetingLinkInfo onSubmit={handleSubmit} meetingLinkInfo={meetingLinkInfo} editable={editable} setEditable={setEditable} staffs={meetingLink?.staffs ?? []} />
}
